import {
  ChangeDetectionStrategy,
  Component,
  input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
  ButtonElementMetaUiFeatures,
  FormState,
  FormStateContainer,
  getBackground,
  makeContainerVisualEffectStyles,
  makeFontStyles,
  makeFontStyleStyles,
  makeLabelHorizontalAlignStyles,
} from '@blaze-shared/elements-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';

import { CanvasElementBaseComponent } from '../__base/base.component';

// TODO: test
@UntilDestroy()
@Component({
  selector: 'be-button',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--button-element-padding]': 'padding()',
  },
  standalone: false,
})
export class ButtonComponent
  extends CanvasElementBaseComponent<'button'>
  implements OnChanges, OnInit
{
  formState = input<FormState<FormStateContainer>>();
  delayClick = input(false);

  private requiredDisableSubmit = false;

  label!: string;
  icon!: string;
  wrapperStyles: Record<string, string> | null = null;
  buttonStyles!: Record<string, string>;
  iconSize!: string;
  showLoader = false;
  padding = signal('8px');
  isDisabled = signal(false);
  isLoaderVisible = signal(false);

  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.button?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      this.label = this.props?.content?.button?.label || '';
      this.icon = this.props?.content?.button?.icon || '';
      this.showLoader = this.props?.content?.button?.showLoader || false;
      this.updateStyles();
    }
    if (changes?.['formState']) {
      this.updateDisabled();
    }
  }

  ngOnInit(): void {
    this.getElementUiFeatures<ButtonElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.requiredDisableSubmit = features.requiredDisableSubmit ?? false;
        this.updateDisabled();
        safeDetectChanges(this.cdr);
      });
  }

  emitClick(toolbarTriggered = false): void {
    if ((this.isInEditor && !toolbarTriggered) || this.isDisabled() || this.isLoaderVisible()) {
      return;
    }
    if (this.delayClick()) {
      this.isLoaderVisible.set(true);
    }
    this.emitDelayedClick(toolbarTriggered);
  }

  private emitDelayedClick(toolbarTriggered = false): void {
    if (this.delayClick()) {
      setTimeout(() => this.emitDelayedClick(toolbarTriggered), 50);
      return;
    }
    this.output.emit({
      eventType: 'click',
      data: undefined,
      schema: null,
      isInitialData: false,
    });
    if (this.isLoaderVisible()) {
      this.isLoaderVisible.set(false);
    }
  }

  private updateDisabled(): void {
    const formState = this.formState();
    this.isDisabled.set(
      this.requiredDisableSubmit &&
        !!formState &&
        !formState.valid &&
        !!formState.errors &&
        Object.values(formState.errors).some((err) => err['required']),
    );
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { button, font, label } = style || {};
    if (!style || !button || !font || !label) {
      return;
    }

    this.padding.set(button.offset.padding ?? '8px');
    const wrapperStyles: Record<string, string> = {
      ['min-height']: this.isUsingOldPaddingStrategy ? '16px' : '40px',
    };
    const buttonStyles: Record<string, string> = {
      color: label.color.color1,
      background: getBackground(button.fill),
      ...makeFontStyles(font),
      ...makeFontStyleStyles(font?.style, '700'),
      ...makeLabelHorizontalAlignStyles(label?.align || 'center'),
      ['font-weight']: this.fontsService.getFontWeight(font.fontFamily, font.fontWeight),
    };
    if (content?.button?.iconPosition === 'right') {
      buttonStyles['flex-direction'] = `row-reverse`;
    }
    if (button.outline.selectorType !== 'none' && button.outlineWidth !== '0px') {
      wrapperStyles['border'] = `${button.outlineWidth} solid ${button.outline.color1}`;
    }
    const effectsStyles =
      button.visualEffect?.type === 'inset-shadow' ? buttonStyles : wrapperStyles;
    Object.assign(effectsStyles, makeContainerVisualEffectStyles(button.visualEffect));
    if (button.radius !== '0px') {
      effectsStyles['border-radius'] = button.radius;
    }

    this.wrapperStyles = wrapperStyles;
    this.buttonStyles = buttonStyles;
  }
}
